import {
  changeCompany,
  declineCompanyInvitation,
  getJoinPreflightAction,
} from "@/lib/api/handlers/CompanyRoleHandling";
import { JoinPreflightResponse } from "@/lib/api/clients/CompanyRoleClient";
import { changeEventHostCompany } from "@/lib/api/handlers/EventHandling";
import { getCompany } from "@/lib/api/handlers/CompanyHandling";
import { hasActiveSubscriptions } from "@/lib/api/handlers/SubscriptionHandling";
import {
  acceptEventInvitation,
  declineEventInvitation,
} from "@/lib/api/handlers/EventRoleHandling";
import { isServerErr, ServerError, throwServerError } from "@/lib/api/errors/ErrorHandler";
import { UserLog } from "@/types/UserLog";
import { useQueryClient } from "@tanstack/react-query";
import type { LossesAck } from "@/lib/legal/dissolveLossesAck";

export type PreflightResult = {
  preflight: JoinPreflightResponse;
  notificationId: string;
  notification: UserLog;
  /**
   * Offboarding: the CURRENT organisation's name for `joinLosesEvents`. Read
   * only when the preflight says events are lost; undefined when that read
   * failed (the dialog then says "your current organisation").
   */
  organisationName?: string;
};

/**
 * Offboarding MED-5: joining another organisation ends the caller's roles in
 * the current one's events. Only the backend's own `offboardingEnabled ===
 * true` makes `eventsLost` count; absent = an older backend, nothing lost.
 */
export function preflightLosesEvents(preflight: JoinPreflightResponse): boolean {
  return (
    preflight.offboardingEnabled === true &&
    typeof preflight.eventsLost === "number" &&
    preflight.eventsLost > 0
  );
}

/** The current organisation's name, or undefined — never fails the join. */
async function currentOrganisationName(): Promise<string | undefined> {
  try {
    const company = await getCompany("name");
    if (isServerErr(company)) return undefined;
    const name = company?.name?.trim();
    return name ? name : undefined;
  } catch {
    return undefined;
  }
}

/**
 * The outcome of a company switch, carrying the backend error code on failure.
 *
 * It used to be a bare boolean, so every cause collapsed into one sentence:
 * "Wechsel der Organisation fehlgeschlagen. Bitte versuche es erneut." The
 * code went to `console.error` and nowhere else. That mattered more than a
 * missing detail — the switch can fail deterministically (a duplicate-key
 * error surfacing as `nameAlreadyExists` could never succeed on a retry), and
 * the retry the message invited was what destroyed the user's session.
 */
export type BackendFailure = { code: string; detail?: string };

export type CompanyChangeOutcome = { ok: true } | ({ ok: false } & BackendFailure);

/** Marker returned by `actionAccept` when a company switch failed. */
export type CompanyChangeFailure = {
  kind: "companyChangeFailed";
} & BackendFailure;

/** Marker returned when an accept/decline that is NOT a company switch failed. */
export type NotificationActionFailure = {
  kind: "actionFailed";
} & BackendFailure;

/**
 * `detail` travels alongside the code because `translateBackendError` appends
 * it for catch-all codes like `validationError` — dropping it would leave the
 * user with a bare "Validation error" and no field-level reason, which defeats
 * the point of surfacing the code at all.
 */
function failureOf(error: unknown): BackendFailure {
  if (isServerErr(error)) {
    return {
      code: error.message ?? "unknown",
      detail: typeof error.detail === "string" ? error.detail : undefined,
    };
  }
  if (error instanceof Error && error.message) return { code: error.message };
  return { code: "unknown" };
}

export const usePNotifications = (
  handleNotificationUpdate: (
    id: string,
    actionMethod: (
      id: string
    ) => Promise<void | ServerError> | Promise<string | ServerError>,
    notification: UserLog
  ) => Promise<void>
) => {
  const queryClient = useQueryClient();
  // Accepting an invite/transfer adds an event to the user's dashboard, but
  // the grid/count/quota caches live outside this component — mark them stale
  // so the dashboard doesn't show the pre-accept snapshot for 30s.
  const invalidateEventListCaches = () => {
    void queryClient.invalidateQueries({ queryKey: ["root"] });
    void queryClient.invalidateQueries({ queryKey: ["userSubscription"] });
  };
  /**
   * Call changeCompany directly — NOT through handleNotificationUpdate.
   * handleNotificationUpdate swallows errors (catches and resolves normally),
   * making it impossible to know if the company change actually succeeded.
   * After a company change the entire page context is invalid anyway,
   * so notification list updates are pointless — we must full-reload.
   *
   * Returns `{ok: true}`, or the backend error code so the caller can say
   * something truthful about WHY it failed.
   */
  async function doChangeCompany(
    newCompanyId: string,
    lossesAck: LossesAck | null,
  ): Promise<CompanyChangeOutcome> {
    try {
      const result = await changeCompany(newCompanyId, { lossesAck });
      if (result && "isServerError" in result) {
        return { ok: false, ...failureOf(result) };
      }
      return { ok: true };
    } catch (error) {
      return { ok: false, ...failureOf(error) };
    }
  }

  async function actionAccept(
    type: string,
    id: string,
    notification: UserLog,
  ): Promise<
    | PreflightResult
    | "companyChanged"
    | CompanyChangeFailure
    | NotificationActionFailure
    | "transferNeedsSubscription"
    | void
    | null
  > {
    if (type === "EVENT_INVITATION") {
      // `acceptEventInvitation` RETURNS a ServerError instead of throwing, and
      // handleNotificationUpdate reads "did not throw" as success — so a
      // failed accept still removed the invitation from the list and the user
      // was told nothing. Same shape the transfer branch below already guards
      // against; both doors need it.
      //
      // Throwing alone is only half the cure: handleNotificationUpdate catches
      // and parks the message in `notificationState.error`, which no component
      // in the repo reads. Without carrying the failure out, the notification
      // correctly survives — and the click looks completely dead. Same idiom as
      // `subscriptionRejected` in the transfer branch below.
      let failure: BackendFailure | null = null;
      const result = await handleNotificationUpdate(
        id,
        async (eventId) => {
          const r = await acceptEventInvitation(eventId);
          if (isServerErr(r)) {
            failure = failureOf(r);
            throwServerError(r);
          }
          return r;
        },
        notification
      );
      if (failure) return { kind: "actionFailed", ...(failure as BackendFailure) };
      invalidateEventListCaches();
      return result;
    }
    if (type === "EVENT_TRANSFER_INVITATION") {
      // Accepting a transfer moves the event INTO the accepting company, and
      // the backend rejects that for free plans (`subscriptionRequired`).
      // Pre-check here so the user gets an upgrade dialog instead of a dead
      // click; on check failure fall through and let the backend enforce.
      const hasSub = await hasActiveSubscriptions();
      if (hasSub === false) return "transferNeedsSubscription";

      // The handler returns ServerError instead of throwing, which
      // handleNotificationUpdate would treat as success — silently removing
      // the notification while the transfer never happened. Re-throw so the
      // notification survives a failed accept, and surface the free-plan
      // rejection (pre-check raced a subscription expiry) as the upgrade
      // dialog.
      let subscriptionRejected = false;
      let transferFailure: BackendFailure | null = null;
      const result = await handleNotificationUpdate(
        id,
        async (eventId) => {
          const r = await changeEventHostCompany(eventId);
          if (isServerErr(r)) {
            if (r.message === "subscriptionRequired") subscriptionRejected = true;
            else transferFailure = failureOf(r);
            throwServerError(r);
          }
        },
        notification
      );
      if (subscriptionRejected) return "transferNeedsSubscription";
      // Every other failure used to end here as a silent `void`: the throw kept
      // the notification (right) and told the user nothing (wrong). This door
      // was the one the sibling comment above claimed parity with — it did not
      // have it.
      if (transferFailure)
        return { kind: "actionFailed", ...(transferFailure as BackendFailure) };
      invalidateEventListCaches();
      return result;
    }
    if (type === "COMPANY_INVITATION") {
      // Preflight check — returns data for the component to show warnings
      const preflight = await getJoinPreflightAction();
      if (preflight && "isServerError" in preflight) {
        // FAIL CLOSED. This used to "proceed without warning (best-effort)":
        // when the preflight call failed we switched anyway, skipping the
        // dialog that tells a solo owner their organisation is about to be
        // dissolved. Losing the warning is not an acceptable degradation for
        // the most destructive action in the product — surface the error and
        // let the user decide.
        return { kind: "companyChangeFailed", ...failureOf(preflight) };
      }

      // If no warnings, proceed directly. Losing the current organisation's
      // events is a warning too (offboarding MED-5): the backend answers the
      // three old fields false for a non-owner, who used to switch unwarned.
      const losesEvents = preflightLosesEvents(preflight);
      if (
        !preflight.blocked &&
        !preflight.hasActiveSubscriptions &&
        !preflight.willDeleteCompany &&
        !losesEvents
      ) {
        // No dialog, so no loss notice was shown: never acknowledged.
        const outcome = await doChangeCompany(id, null);
        return outcome.ok
          ? "companyChanged"
          : { kind: "companyChangeFailed", code: outcome.code, detail: outcome.detail };
      }

      // Return preflight data for the component to show a dialog
      return {
        preflight,
        notificationId: id,
        notification,
        ...(losesEvents ? { organisationName: await currentOrganisationName() } : {}),
      };
    }

    return null;
  }

  async function actionReject(
    type: string,
    id: string,
    notification: UserLog,
  ): Promise<NotificationActionFailure | void | null> {
    // Both decline doors hit the same returned-not-thrown ServerError trap:
    // handleNotificationUpdate reads "did not throw" as success and drops the
    // notification even though nothing happened. Throwing keeps it; carrying
    // the failure out is what lets the UI say so — the catch inside
    // handleNotificationUpdate writes to a state field nobody renders.
    let failure: BackendFailure | null = null;
    const capture = async <T,>(run: () => Promise<T | ServerError>) => {
      const r = await run();
      if (isServerErr(r)) {
        failure = failureOf(r);
        throwServerError(r);
      }
      return r;
    };

    if (["EVENT_INVITATION", "EVENT_TRANSFER_INVITATION"].includes(type)) {
      await handleNotificationUpdate(
        id,
        async (eventId) => capture(() => declineEventInvitation(eventId, type)),
        notification
      );
      return failure ? { kind: "actionFailed", ...(failure as BackendFailure) } : null;
    }
    if (type === "COMPANY_INVITATION") {
      await handleNotificationUpdate(
        id,
        async (companyId) => capture(() => declineCompanyInvitation(companyId)),
        notification
      );
      return failure ? { kind: "actionFailed", ...(failure as BackendFailure) } : null;
    }

    return null;
  }

  /**
   * Called from the preflight confirmation dialog. Carries the backend error
   * code out so the dialog can name the reason instead of inviting a retry.
   *
   * `lossesAck` (PB-CDP-7): set exactly when the dialog showed a loss notice
   * and the user ticked the box under it — the switch door refuses a
   * dissolve with losses without it, and records it.
   */
  async function confirmCompanyJoin(
    newCompanyId: string,
    { lossesAck = null }: { lossesAck?: LossesAck | null } = {},
  ): Promise<CompanyChangeOutcome> {
    return await doChangeCompany(newCompanyId, lossesAck);
  }

  return { actionAccept, actionReject, confirmCompanyJoin };
};
